<template>
    <div class="request-list">
        <Table
            :columns="tblColumns"
            :data="requestsDataList"
            :loading="isLoading"
            no-data-text="No leave requests for this year"
            class="request-list__table"
        />

        <div class="request-list__cards">
            <Spin v-if="isLoading" fix />
            <p v-if="!requestsDataList.length" class="request-list__empty">No leave requests for this year</p>
            <div v-for="row in requestsDataList" :key="row._id || row.id" class="request-card">
                <div class="request-card__head">
                    <div>
                        <div class="request-card__dates">
                            {{ formatDate(row.requestedStart) }} to {{ formatDate(row.requestedEnd) }}
                        </div>
                        <div class="request-card__meta">{{ formatType(row) }} · {{ formatMode(row) }}</div>
                    </div>
                    <span :class="['request-status', `request-status--${row.status}`]">
                        <span class="request-status__dot" />{{ formatStatus(row.status) }}
                    </span>
                </div>
                <div class="request-card__foot">
                    <span class="request-card__filed">Filed {{ formatDate(row.createdAt) }}</span>
                    <div class="request-actions">
                        <Button size="small" icon="md-eye" @click="handleView(row)">View</Button>
                        <template v-if="isPending(row)">
                            <Button size="small" icon="md-create" @click="handleEdit(row)">Edit</Button>
                            <Button
                                size="small"
                                icon="ios-remove-circle-outline"
                                class="request-actions__cancel"
                                @click="handleCancel(row)"
                            >
                                Cancel
                            </Button>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <Page
            v-if="totalCount > limit"
            :total="totalCount"
            :current="currentPage"
            :page-size="limit"
            size="small"
            class="request-list__pagination"
            @on-change="handleOnpageChanged"
        />
        <ViewRequestModal ref="ViewRequest" />
        <UpdateRequestModal ref="UpdateRequest" @create="handleReloadList" />
        <DeleteRequestModal ref="DeleteRequest" @create="handleReloadList" />
    </div>
</template>

<script>
import moment from 'moment';
import { formatLeaveType } from '../../shared/tableRender';
import ViewRequestModal from './ViewRequestModal';
import UpdateRequestModal from './UpdateRequestModal';
import DeleteRequestModal from './DeleteRequestModal';

const LEAVE_MODES = {
    FULL: 'Full day',
    AM: 'Half day (AM)',
    PM: 'Half day (PM)',
};

export default {
    components: {
        ViewRequestModal,
        UpdateRequestModal,
        DeleteRequestModal,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        requestsDataList: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        limit: {
            type: Number,
            default: 10,
        },
    },

    data() {
        return {
            tblColumns: [
                {
                    title: 'Filed On',
                    key: 'createdAt',
                    minWidth: 120,
                    render: (h, params) =>
                        h('span', { class: 'request-list__dates' }, this.formatDate(params.row.createdAt)),
                },
                {
                    title: 'Leave Start',
                    key: 'requestedStart',
                    minWidth: 120,
                    render: (h, params) => h('span', this.formatDate(params.row.requestedStart)),
                },
                {
                    title: 'Leave End',
                    key: 'requestedEnd',
                    minWidth: 120,
                    render: (h, params) => h('span', this.formatDate(params.row.requestedEnd)),
                },
                {
                    title: 'Type',
                    key: 'type',
                    minWidth: 150,
                    render: (h, params) => h('span', this.formatType(params.row)),
                },
                {
                    title: 'Mode',
                    key: 'mode',
                    minWidth: 120,
                    render: (h, params) => h('span', this.formatMode(params.row)),
                },
                {
                    title: 'Status',
                    key: 'status',
                    minWidth: 130,
                    render: (h, params) =>
                        h('span', { class: ['request-status', `request-status--${params.row.status}`] }, [
                            h('span', { class: 'request-status__dot' }),
                            this.formatStatus(params.row.status),
                        ]),
                },
                {
                    title: 'Actions',
                    width: 160,
                    align: 'right',
                    render: (h, params) => {
                        const iconButton = (content, icon, onClick, extraClass = '') =>
                            h('Tooltip', { props: { placement: 'top', content, transfer: true } }, [
                                h('Button', {
                                    class: ['request-actions__icon', extraClass],
                                    props: { icon, shape: 'circle' },
                                    on: { click: onClick },
                                }),
                            ]);
                        const buttons = [iconButton('View', 'md-eye', () => this.handleView(params.row))];
                        if (this.isPending(params.row)) {
                            buttons.push(
                                iconButton('Edit', 'md-create', () => this.handleEdit(params.row)),
                                iconButton(
                                    'Cancel',
                                    'ios-remove-circle-outline',
                                    () => this.handleCancel(params.row),
                                    'request-actions__cancel'
                                )
                            );
                        }
                        return h('div', { class: 'request-actions' }, buttons);
                    },
                },
            ],
        };
    },

    methods: {
        formatDate(date) {
            return date ? moment(date).utc().format('YYYY-MM-DD') : '—';
        },
        formatType(row) {
            return formatLeaveType(row.requestedData && row.requestedData.type);
        },
        formatMode(row) {
            const mode = row.requestedData && row.requestedData.mode;
            if (!mode) return '—';
            return LEAVE_MODES[mode.toUpperCase()] || mode;
        },
        formatStatus(status) {
            if (!status) return '—';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        isPending(row) {
            return !!row.status && row.status.includes('pending');
        },
        handleView(row) {
            this.$refs.ViewRequest.openModalForReadOnly(row);
        },
        handleEdit(row) {
            this.$refs.UpdateRequest.openModal(row, false);
        },
        handleCancel(row) {
            this.$refs.DeleteRequest.openModal(row, 'delete');
        },
        handleReloadList() {
            this.$emit('reload');
        },
        handleOnpageChanged(page) {
            this.$emit('on-page-change', page);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../shared/request-table.scss';
</style>

<style lang="scss">
ul.pagination.ivu-page {
    text-align: end;
}
.requestsList button {
    width: 25px;
    margin-left: 10px;
}
.requestsList i.ivu-icon {
    margin-left: -12.5px;
}
.btn-disable {
    cursor: not-allowed;
    pointer-events: none;

    /*Button disabled - CSS color class*/
    color: #c0c0c0;
    background-color: #ffffff;
}
.statusLabel {
    display: inline-block;
    color: #fff;
    border-radius: 5px;
    box-shadow: 0 0 5px -1px rgba(0, 0, 0, 0.2);
    vertical-align: middle;
    max-width: 250px;
    padding: 5px;
    text-align: center;
}
.success {
    background: #49e97e;
    font-weight: bold;
}
.warning {
    background: #ffc107;
    font-weight: bold;
}
.danger {
    background: #e9495d;
    font-weight: bold;
}
.primary {
    background: #211841;
}
.secondary {
    background: #828899;
}
.btn-hide {
    display: none;
}
</style>
